import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { SlidersHorizontal, X, ChevronRight } from "lucide-react";
import { api, formatINR } from "../lib/api";
import { useShop } from "../context/ShopContext";
import ProductCard from "../components/ProductCard";
import JsonLd, { SITE_URL } from "../components/JsonLd";

const SORTS = [
  { value: "newest", label: "Newest" },
  { value: "popular", label: "Popular" },
  { value: "price_asc", label: "Price: Low to High" },
  { value: "price_desc", label: "Price: High to Low" },
];

export default function Shop() {
  const { categories } = useShop();
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const category = params.get("category") || "";
  const subcategory = params.get("subcategory") || "";
  const search = params.get("search") || "";
  const sort = params.get("sort") || "newest";
  const maxPrice = params.get("max_price") || "";
  const inStock = params.get("in_stock") === "true";

  const activeCategory = categories.find((c) => c.slug === category);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const q = {};
      if (category) q.category = category;
      if (subcategory) q.subcategory = subcategory;
      if (search) q.search = search;
      if (sort) q.sort = sort;
      if (maxPrice) q.max_price = maxPrice;
      if (inStock) q.in_stock = true;
      const r = await api.get("/products", { params: q });
      setProducts(r.data);
    } finally { setLoading(false); }
  }, [category, subcategory, search, sort, maxPrice, inStock]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const setParam = (key, val) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      if (val === "" || val === null || val === undefined || val === false) next.delete(key);
      else next.set(key, val);
      return next;
    });
  };

  const setParamsBatch = (updates) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [key, val] of Object.entries(updates)) {
        if (val === "" || val === null || val === undefined || val === false) next.delete(key);
        else next.set(key, val);
      }
      return next;
    });
  };

  const clearAll = () => setParams(new URLSearchParams());

  let bannerTitle = "All Products";
  if (activeCategory) bannerTitle = activeCategory.name;
  else if (search) bannerTitle = `Search: "${search}"`;

  // Breadcrumb segments — last item is "current" (not a link)
  const crumbs = [
    { label: "Home", to: "/" },
    { label: "Shop", to: "/shop" },
  ];
  if (activeCategory) {
    crumbs.push({ label: activeCategory.name, to: `/shop?category=${activeCategory.slug}` });
    if (subcategory) crumbs.push({ label: subcategory });
  } else if (search) {
    crumbs.push({ label: `Search: "${search}"` });
  }

  // BreadcrumbList JSON-LD for SEO rich results
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.label,
      ...(c.to ? { item: `${SITE_URL}${c.to}` } : {}),
    })),
  };

  return (
    <div data-testid="shop-page" className="min-h-screen">
      <JsonLd data={breadcrumbSchema} />
      {/* Banner */}
      <section className="relative h-[44vh] flex items-end border-b border-white/5">
        <div className="absolute inset-0">
          <img
            src={activeCategory?.image || "https://images.unsplash.com/photo-1558981852-426c6c22a060?w=2000"}
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/60 to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pb-12 w-full">
          <nav data-testid="shop-breadcrumbs" aria-label="Breadcrumb" className="flex flex-wrap items-center gap-1.5 text-xs font-display tracking-widest uppercase mb-4">
            {crumbs.map((c, i) => {
              const isLast = i === crumbs.length - 1;
              return (
                <span key={c.label} className="flex items-center gap-1.5">
                  {isLast || !c.to ? (
                    <span data-testid={`crumb-${i}`} className="text-[#D96A1B]" aria-current="page">{c.label}</span>
                  ) : (
                    <Link data-testid={`crumb-${i}`} to={c.to} className="text-white/55 hover:text-white transition-colors">{c.label}</Link>
                  )}
                  {!isLast && <ChevronRight size={12} className="text-white/30" />}
                </span>
              );
            })}
          </nav>
          <h1 className="font-display text-5xl sm:text-7xl uppercase tracking-tighter">
            {bannerTitle}
          </h1>
          {activeCategory && <p className="mt-3 text-white/70 font-body max-w-xl">{activeCategory.tagline}</p>}
        </div>
      </section>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 lg:py-14 grid lg:grid-cols-12 gap-10">
        {/* Filters Desktop */}
        <aside className="hidden lg:block lg:col-span-3">
          <FilterPanel
            category={category} setParam={setParam} setParamsBatch={setParamsBatch} subcategory={subcategory}
            maxPrice={maxPrice} inStock={inStock} clearAll={clearAll} categories={categories}
          />
        </aside>

        {/* Mobile Filters */}
        {filtersOpen && (
          <div className="lg:hidden fixed inset-0 z-50 bg-[#0A0A0A] overflow-y-auto p-6" data-testid="mobile-filters">
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-display text-xl uppercase tracking-tight">Filters</h3>
              <button onClick={() => setFiltersOpen(false)}><X /></button>
            </div>
            <FilterPanel
              category={category} setParam={setParam} setParamsBatch={setParamsBatch} subcategory={subcategory}
              maxPrice={maxPrice} inStock={inStock} clearAll={clearAll} categories={categories}
            />
          </div>
        )}

        {/* Grid */}
        <main className="lg:col-span-9">
          <div className="flex items-center justify-between mb-6 gap-4">
            <button
              data-testid="open-filters-mobile"
              onClick={() => setFiltersOpen(true)}
              className="lg:hidden btn-ghost py-2 px-4 text-xs flex items-center gap-2"
            >
              <SlidersHorizontal size={14} /> Filters
            </button>
            <p className="text-sm text-white/60 font-body" data-testid="results-count">
              {loading ? "Loading..." : `${products.length} products`}
            </p>
            <select
              data-testid="sort-select"
              value={sort}
              onChange={(e) => setParam("sort", e.target.value)}
              className="bg-[#111] border border-white/15 px-3 py-2 text-sm font-body focus:outline-none focus:border-[#D96A1B]"
            >
              {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-5">
              {["sk1","sk2","sk3","sk4","sk5","sk6"].map((k) => (
                <div key={k} className="aspect-[4/5] bg-[#111] animate-pulse" />
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="py-20 text-center text-white/60 font-body">
              No products found. <button onClick={clearAll} className="text-[#D96A1B] hover:underline ml-2">Clear filters</button>
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
              {products.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

function FilterPanel({ category, setParam, setParamsBatch, subcategory, maxPrice, inStock, clearAll, categories }) {
  const activeCategory = categories.find((c) => c.slug === category);
  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h3 className="font-display uppercase tracking-widest text-sm text-white/80">Filters</h3>
        <button data-testid="clear-filters" onClick={clearAll} className="text-xs text-[#D96A1B] hover:underline">Clear All</button>
      </div>

      <div>
        <div className="overline mb-3">Category</div>
        <ul className="space-y-2">
          <li>
            <button
              data-testid="filter-cat-all"
              onClick={() => setParamsBatch({ category: "", subcategory: "" })}
              className={`text-sm font-body ${!category ? "text-[#D96A1B]" : "text-white/70 hover:text-white"}`}
            >All Categories</button>
          </li>
          {categories.map((c) => (
            <li key={c.slug}>
              <button
                data-testid={`filter-cat-${c.slug}`}
                onClick={() => setParamsBatch({ category: c.slug, subcategory: "" })}
                className={`text-sm font-body ${category === c.slug ? "text-[#D96A1B]" : "text-white/70 hover:text-white"}`}
              >{c.name}</button>
            </li>
          ))}
        </ul>
      </div>

      {activeCategory && (
        <div>
          <div className="overline mb-3">Sub-Category</div>
          <ul className="space-y-2">
            <li>
              <button
                onClick={() => setParam("subcategory", "")}
                className={`text-sm font-body ${!subcategory ? "text-[#D96A1B]" : "text-white/70 hover:text-white"}`}
              >All</button>
            </li>
            {activeCategory.subs.map((s) => (
              <li key={s}>
                <button
                  data-testid={`filter-sub-${s.replace(/\s+/g, '-').toLowerCase()}`}
                  onClick={() => setParam("subcategory", s)}
                  className={`text-sm font-body ${subcategory === s ? "text-[#D96A1B]" : "text-white/70 hover:text-white"}`}
                >{s}</button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div>
        <div className="overline mb-3">Max Price</div>
        <input
          data-testid="filter-max-price"
          type="range" min="500" max="25000" step="500"
          value={maxPrice || 25000}
          onChange={(e) => setParam("max_price", e.target.value)}
          className="w-full accent-[#D96A1B]"
        />
        <div className="flex justify-between text-xs text-white/50 mt-2 font-body">
          <span>{formatINR(500)}</span>
          <span className="text-[#D96A1B]">Up to {formatINR(maxPrice || 25000)}</span>
        </div>
      </div>

      <div>
        <div className="overline mb-3">Availability</div>
        <label className="flex items-center gap-3 text-sm font-body cursor-pointer">
          <input
            data-testid="filter-instock"
            type="checkbox" checked={inStock}
            onChange={(e) => setParam("in_stock", e.target.checked)}
            className="accent-[#D96A1B] w-4 h-4"
          />
          In Stock Only
        </label>
      </div>
    </div>
  );
}
